import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { calendarAction, useBoard, type CalEvent } from '../../lib/data';
import { supabase } from '../../lib/supabase';
import { fDT } from '../../lib/format';
import { Icon } from '../../ui/icons';
import { CloseX, useDrawer, useToast } from '../../ui/kit';

const pad = (n: number) => String(n).padStart(2, '0');
const localDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const localTime = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

function nextHalfHour() {
  const d = new Date();
  d.setMinutes(d.getMinutes() < 30 ? 30 : 60, 0, 0);
  return d;
}

export function EventDrawer({
  id,
  start,
  leadId,
}: {
  id?: string | undefined;
  start?: string | undefined;
  leadId?: string | undefined;
}) {
  const existing = useQuery({
    queryKey: ['calendar', 'event', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('calendar_events')
        .select(
          'id, google_id, status, summary, description, location, start_at, end_at, all_day, attendees, html_link, meet_link, lead_id',
        )
        .eq('id', id!)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data as CalEvent | null;
    },
    enabled: Boolean(id),
  });

  if (id && existing.isLoading) {
    return (
      <div className="dhead">
        <h2 id="dtitle">Loading…</h2>
        <CloseX />
      </div>
    );
  }
  if (id && !existing.data) {
    return (
      <div className="dhead">
        <h2 id="dtitle">Event not found</h2>
        <CloseX />
      </div>
    );
  }
  return <EventForm event={existing.data ?? null} start={start} leadId={leadId} />;
}

function EventForm({
  event,
  start,
  leadId,
}: {
  event: CalEvent | null;
  start?: string | undefined;
  leadId?: string | undefined;
}) {
  const { open, close } = useDrawer();
  const toast = useToast();
  const board = useBoard();
  const linkedLead = board.data?.find((l) => l.id === (event?.lead_id ?? leadId));

  const initial = useMemo(() => {
    const s = event ? new Date(event.start_at) : start ? new Date(start) : nextHalfHour();
    const e = event ? new Date(event.end_at) : new Date(s.getTime() + 30 * 60_000);
    const guests = event
      ? event.attendees.map((a) => a.email).filter((m) => !m.endsWith('@ouradent.com'))
      : linkedLead?.email
        ? [linkedLead.email]
        : [];
    return {
      summary: event?.summary ?? (linkedLead ? `Call with ${linkedLead.clinic}` : ''),
      date: localDate(s),
      from: localTime(s),
      to: localTime(e),
      guests: guests.join(', '),
      location: event?.location ?? '',
      description: event?.description ?? '',
      meet: !event,
      notify: true,
    };
  }, [event, start, linkedLead]);

  const [f, setF] = useState(initial);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const set =
    (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setF({
        ...f,
        [k]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value,
      });

  async function save() {
    const s = new Date(`${f.date}T${f.from}`);
    const e = new Date(`${f.date}T${f.to}`);
    if (!f.summary.trim()) return setErr('Add a title.');
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()))
      return setErr('Add a date and times.');
    if (e <= s) return setErr('The end has to be after the start.');
    const guests = f.guests
      .split(/[,;\s]+/)
      .map((g) => g.trim().toLowerCase())
      .filter(Boolean);
    const bad = guests.find((g) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(g));
    if (bad) return setErr(`"${bad}" is not an email address.`);

    setBusy(true);
    setErr('');
    try {
      await calendarAction({
        action: event ? 'update' : 'create',
        ...(event ? { googleId: event.google_id } : {}),
        event: {
          summary: f.summary.trim(),
          description: f.description.trim() || null,
          location: f.location.trim() || null,
          start: s.toISOString(),
          end: e.toISOString(),
          attendees: guests,
          addMeet: !event && f.meet,
          notify: f.notify && guests.length > 0,
        },
      });
      toast(event ? 'Event updated in Google Calendar' : 'Event added to Google Calendar');
      close();
    } catch (x) {
      setErr(x instanceof Error ? x.message : String(x));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!event) return;
    if (!window.confirm('Delete this event from the Ouradent Google calendar?')) return;
    setBusy(true);
    try {
      await calendarAction({ action: 'delete', googleId: event.google_id, notify: f.notify });
      toast('Event deleted');
      close();
    } catch (x) {
      setErr(x instanceof Error ? x.message : String(x));
      setBusy(false);
    }
  }

  return (
    <>
      <div className="dhead">
        <span className="vid" style={{ width: 52, height: 52 }}>
          <Icon name="calendar" />
        </span>
        <div>
          <h2 id="dtitle">{event ? (event.summary ?? '(no title)') : 'New event'}</h2>
          <div className="sm">
            {event ? fDT(event.start_at) : 'Lands in the Ouradent Google calendar'}
            {linkedLead ? ` · ${linkedLead.clinic}` : ''}
          </div>
        </div>
        <CloseX />
      </div>
      <div className="dbody">
        {event && (event.meet_link || event.html_link || linkedLead) && (
          <div className="blk" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {event.meet_link && (
              <a className="btn primary" href={event.meet_link} target="_blank" rel="noreferrer">
                Join Google Meet
              </a>
            )}
            {event.html_link && (
              <a className="btn" href={event.html_link} target="_blank" rel="noreferrer">
                Open in Google <Icon name="ext" />
              </a>
            )}
            {linkedLead && (
              <button className="btn" onClick={() => open({ type: 'lead', id: linkedLead.id })}>
                Open {linkedLead.clinic}
              </button>
            )}
          </div>
        )}
        <div className="panel grid2">
          <label className="field full">
            <span>Title</span>
            <input type="text" value={f.summary} onChange={set('summary')} />
          </label>
          <label className="field">
            <span>Date</span>
            <input type="date" value={f.date} onChange={set('date')} />
          </label>
          <div className="field" style={{ display: 'flex', gap: 8 }}>
            <label className="field" style={{ flex: 1 }}>
              <span>From</span>
              <input type="time" value={f.from} onChange={set('from')} />
            </label>
            <label className="field" style={{ flex: 1 }}>
              <span>To</span>
              <input type="time" value={f.to} onChange={set('to')} />
            </label>
          </div>
          <label className="field full">
            <span>Guests (emails, comma separated)</span>
            <input
              type="text"
              value={f.guests}
              onChange={set('guests')}
              placeholder="dr.kaya@clinic.com"
            />
          </label>
          <label className="field full">
            <span>Location</span>
            <input type="text" value={f.location} onChange={set('location')} />
          </label>
          <label className="field full">
            <span>Notes</span>
            <textarea rows={4} value={f.description} onChange={set('description')} />
          </label>
          {!event && (
            <label className="check full">
              <input type="checkbox" checked={f.meet} onChange={set('meet')} /> Add a Google Meet
              link
            </label>
          )}
          <label className="check full">
            <input type="checkbox" checked={f.notify} onChange={set('notify')} /> Email the{' '}
            {event ? 'change' : 'invitation'} to guests
          </label>
        </div>
      </div>
      <div className="dfoot">
        {err && (
          <p className="err" role="alert">
            {err}
          </p>
        )}
        <button className="btn primary big" onClick={() => void save()} disabled={busy}>
          {busy ? 'Saving…' : event ? 'Save changes' : 'Add to calendar'}
        </button>
        {event && (
          <button className="btn ghost" onClick={() => void remove()} disabled={busy}>
            Delete
          </button>
        )}
        <button className="btn ghost" onClick={close}>
          Cancel
        </button>
      </div>
    </>
  );
}
